/*
 * JOOLME appointment slot availability helper.
 * A slot is date + HH:mm (30-min steps). Opening hours:
 *   Sun-Thu 10:00-19:00 (last slot 18:30), Fri 10:00-14:00 (last slot 13:30), Sat closed.
 * Booked-slot data comes from the appointment-slots Supabase edge function; the server
 * (reserve_appointment_slot) is the source of truth and creates or reschedules atomically on submit.
 * The final reserve step FAILS CLOSED: if the service is unreachable, times out, or answers
 * with anything unexpected, the booking is blocked (reason 'network') and the user is offered
 * WhatsApp as a fallback — nothing reaches the CRM without a confirmed reservation.
 * Availability reads (fetchSlots) may still fall back to client-side opening-hours rules so
 * the pickers keep working; the reserve gate is what protects against double booking.
 */
window.JoolmeSlots = (function () {
  var ENDPOINT = 'https://zrgjteovbzksxcixovsf.supabase.co/functions/v1/appointment-slots';
  var OPEN_MINUTES = 10 * 60;
  var SLOT_STEP = 30;

  function pad(n) {
    return String(n).padStart(2, '0');
  }

  function toDateKey(date) {
    return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate());
  }

  function collectBookingAttribution() {
    var allowed = false;
    try {
      allowed = window.Shopify?.customerPrivacy?.marketingAllowed?.() === true;
    } catch (_error) { /* privacy API unavailable */ }

    var stored = {};
    if (allowed) {
      try {
        var parsed = JSON.parse(sessionStorage.getItem('joolme_marketing_attribution') || '{}');
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) stored = parsed;
      } catch (_error) { /* no persisted campaign */ }
    }
    function safe(value, max) {
      return typeof value === 'string' && value.trim().length <= max ? value.trim() : '';
    }
    function cookie(name) {
      if (!allowed) return '';
      var prefix = name + '=';
      var found = document.cookie.split(';').map(function (part) { return part.trim(); })
        .find(function (part) { return part.indexOf(prefix) === 0; });
      return found ? safe(found.slice(prefix.length), 600) : '';
    }
    var gclid = '';
    try {
      var observedAt = Number(localStorage.getItem('gclid_observed_at'));
      if (Number.isFinite(observedAt) && observedAt <= Date.now() &&
          Date.now() - observedAt <= 30 * 24 * 60 * 60 * 1000) {
        gclid = safe(localStorage.getItem('gclid'), 500);
      }
    } catch (_error) { /* storage unavailable */ }
    return {
      marketing_allowed: allowed,
      utm_source: allowed ? safe(stored.utm_source, 255) : '',
      utm_medium: allowed ? safe(stored.utm_medium, 255) : '',
      utm_campaign: allowed ? safe(stored.utm_campaign, 255) : '',
      gclid: gclid,
      fbclid: allowed ? safe(stored.fbclid, 500) : '',
      fbc: allowed ? safe(stored.fbc, 600) : '',
      fbp: cookie('_fbp')
    };
  }

  // One-time early-closing overrides, keyed by YYYY-MM-DD. Value = last selectable slot
  // start in minutes from midnight. Must stay in sync with the server-side
  // appointment_allowed_slots() function (the reserve path re-validates against it).
  var CLOSING_OVERRIDES = {
    '2026-07-12': 16 * 60 + 30, // store closes 17:00 (last slot 16:30)
    '2026-07-13': 16 * 60 + 30,
    '2026-07-14': 16 * 60 + 30
  };

  // Returns the last allowed slot start in minutes, or null if closed. Date-specific
  // override wins, then normal weekly hours (0=Sunday .. 6=Saturday).
  function lastSlotMinutesFor(date) {
    var override = CLOSING_OVERRIDES[toDateKey(date)];
    if (override !== undefined) return override;
    var day = date.getDay();
    if (day === 6) return null;
    if (day === 5) return 13 * 60 + 30;
    return 18 * 60 + 30;
  }

  function hasClosingOverride(date) {
    return CLOSING_OVERRIDES[toDateKey(date)] !== undefined;
  }

  function allowedTimes(date) {
    var last = lastSlotMinutesFor(date);
    if (last === null) return [];
    var times = [];
    for (var m = OPEN_MINUTES; m <= last; m += SLOT_STEP) {
      times.push(pad(Math.floor(m / 60)) + ':' + pad(m % 60));
    }
    return times;
  }

  function isPastSlot(date, time, now) {
    now = now || new Date();
    var dateKey = toDateKey(date);
    var nowKey = toDateKey(now);
    if (dateKey < nowKey) return true;
    if (dateKey > nowKey) return false;
    var parts = time.split(':');
    return (parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10)) <= (now.getHours() * 60 + now.getMinutes());
  }

  // Full slot list for a date: client-side rules (opening hours + past time) always apply;
  // serverSlots (booked info for the same date) can only further restrict, never widen.
  function slotsFor(date, serverSlots) {
    var serverMap = null;
    if (serverSlots && serverSlots.length) {
      serverMap = {};
      serverSlots.forEach(function (s) {
        serverMap[s.time] = !!s.available;
      });
    }
    return allowedTimes(date).map(function (time) {
      var available = !isPastSlot(date, time);
      if (available && serverMap && serverMap[time] === false) available = false;
      return { time: time, available: available };
    });
  }

  // Group slots by hour for the two-select UI: an hour is enabled if any of its minutes is.
  function hourGroups(slots) {
    var groups = [];
    var byHour = {};
    slots.forEach(function (s) {
      var hour = parseInt(s.time.split(':')[0], 10);
      var minute = s.time.split(':')[1];
      if (!byHour[hour]) {
        byHour[hour] = { hour: hour, available: false, minutes: [] };
        groups.push(byHour[hour]);
      }
      byHour[hour].minutes.push({ minute: minute, available: s.available });
      if (s.available) byHour[hour].available = true;
    });
    return groups;
  }

  function hasAvailableSlot(date, serverSlots) {
    return slotsFor(date, serverSlots).some(function (s) { return s.available; });
  }

  function fetchSlots(date) {
    return axios.get(ENDPOINT, { params: { date: toDateKey(date) } })
      .then(function (res) {
        return (res.data && res.data.ok && res.data.slots) ? res.data.slots : null;
      })
      .catch(function () {
        return null;
      });
  }

  function reserve(date, time, payload) {
    var body = Object.assign({ date: toDateKey(date), time: time }, payload || {});
    body.attribution = collectBookingAttribution();
    return axios.post(ENDPOINT, body, { timeout: 15000 })
      .then(function (res) {
        return (res.data && typeof res.data.ok === 'boolean') ? res.data : { ok: false, reason: 'network' };
      })
      .catch(function () {
        return { ok: false, reason: 'network' };
      });
  }

  // Phone normalization for the wizard's phone field. Mobile keyboards and autofill add
  // characters the eye can't see (trailing space after a suggestion, bidi marks from a
  // copied contact) or formats people type naturally (052-2227669, +972 52...). The wizard's
  // step-5 check is /^\d{9,10}$/, so any of those silently kept the button disabled.
  // Normalizes to local digits: Arabic-Indic digits -> ASCII, drop every non-digit,
  // 00972 / 972 international prefix -> leading 0 (only once the number is complete).
  function normalizePhone(value) {
    var s = String(value == null ? '' : value)
      .replace(/[\u0660-\u0669]/g, function (d) { return String(d.charCodeAt(0) - 0x0660); })
      .replace(/[\u06F0-\u06F9]/g, function (d) { return String(d.charCodeAt(0) - 0x06F0); })
      .replace(/\D/g, '');
    if (s.indexOf('00972') === 0) s = s.slice(2);
    if (s.indexOf('972') === 0 && s.length >= 11) {
      s = s.slice(3);
      if (s.charAt(0) !== '0') s = '0' + s;
    }
    return s;
  }

  // Israeli numbers only: mobile / 07x = 10 digits (05x, 07x), landline = 9 digits (02,03,04,08,09).
  function isValidPhone(value) {
    var d = normalizePhone(value);
    return /^0[57]\d{8}$/.test(d) || /^0[23489]\d{7}$/.test(d);
  }

  // 'empty' | 'valid' | 'incomplete' (could still become valid) | 'invalid'
  function phoneStatus(value) {
    var d = normalizePhone(value);
    if (!d) return 'empty';
    if (isValidPhone(d)) return 'valid';
    if (/^(00?)?9(7(2\d{0,9})?)?$/.test(d) && d.length < 12) return 'incomplete'; // mid-typing +972 / 00972
    if (d.charAt(0) !== '0') return 'invalid';
    if (d.length < 2) return 'incomplete';
    var c = d.charAt(1);
    var need = (c === '5' || c === '7') ? 10 : ('23489'.indexOf(c) !== -1 ? 9 : 0);
    if (!need) return 'invalid';
    return d.length < need ? 'incomplete' : 'invalid';
  }

  return {
    toDateKey: toDateKey,
    allowedTimes: allowedTimes,
    isPastSlot: isPastSlot,
    slotsFor: slotsFor,
    hourGroups: hourGroups,
    hasAvailableSlot: hasAvailableSlot,
    hasClosingOverride: hasClosingOverride,
    fetchSlots: fetchSlots,
    reserve: reserve,
    normalizePhone: normalizePhone,
    isValidPhone: isValidPhone,
    phoneStatus: phoneStatus
  };
})();

/*
 * Vue 2 mixin with the whole wizard-side availability wiring, shared by the appointment
 * wizards in templates/page.virtual.experience.liquid and templates/product.liquid.
 * The component must provide: meetingDate, meetingHours, meetingMinutes, step (data),
 * isBtnDisabled (computed), setStep/formatTime/submitForm (methods), and may set
 * slotSource in data to tag reservations. Component-defined options with the same
 * name override the mixin's (Vue merge rules), so templates must not redefine these.
 * Phone field: templates render <joolme-phone-input v-model="clientEmailPhone"> (below);
 * the watcher here is a safety net that keeps clientEmailPhone normalized either way.
 */
window.JoolmeSlotsMixin = {
  data: function () {
    return {
      slotData: null, // { dateKey, slots } from the availability API for the selected date
      reserving: false
    };
  },
  computed: {
    isTodayAvailable: function () {
      return window.JoolmeSlots.hasAvailableSlot(new Date());
    },
    availableSlotList: function () {
      return window.JoolmeSlots.slotsFor(this.meetingDate, this.serverSlots());
    },
    hourOptions: function () {
      return window.JoolmeSlots.hourGroups(this.availableSlotList);
    },
    minuteOptions: function () {
      var self = this;
      var selected = this.hourOptions.find(function (option) { return option.hour == self.meetingHours; });
      return selected ? selected.minutes : [];
    },
    selectedSlotAvailable: function () {
      return this.checkSelectedSlot();
    },
    showEarlyClosingNotice: function () {
      return window.JoolmeSlots.hasClosingOverride(this.meetingDate);
    }
  },
  watch: {
    step: function (val) {
      if (val == 4) {
        this.refreshSlots();
      }
    },
    meetingDate: function () {
      this.refreshSlots();
    },
    meetingHours: function () {
      this.ensureValidSlotSelection();
    },
    meetingMinutes: function () {
      this.ensureValidSlotSelection();
    },
    availableSlotList: {
      immediate: true,
      handler: function () {
        this.ensureValidSlotSelection();
      }
    },
    clientEmailPhone: function (val) {
      if (typeof val !== 'string') return;
      var clean = window.JoolmeSlots.normalizePhone(val);
      if (clean !== val) this.clientEmailPhone = clean;
    }
  },
  methods: {
    handleNextStep: function () {
      if (this.isBtnDisabled || this.reserving) {
        return;
      }
      if (this.step == 4 && !this.checkSelectedSlot()) {
        alert('השעה הזו אינה זמינה, אפשר לבחור שעה אחרת?');
        this.refreshSlots();
        return;
      }
      if (this.step == 5) {
        this.confirmAndSubmit();
        return;
      }
      if (this.step != 6) {
        this.setStep(parseInt(this.step) + 1);
      } else {
        this.setStep(0);
      }
    },
    refreshSlots: function () {
      var self = this;
      var dateKey = window.JoolmeSlots.toDateKey(this.meetingDate);
      window.JoolmeSlots.fetchSlots(this.meetingDate).then(function (slots) {
        self.slotData = slots ? { dateKey: dateKey, slots: slots } : null;
      });
    },
    serverSlots: function () {
      if (this.slotData && this.slotData.dateKey === window.JoolmeSlots.toDateKey(this.meetingDate)) {
        return this.slotData.slots;
      }
      return null;
    },
    checkSelectedSlot: function () {
      if (this.meetingHours === null || this.meetingHours === undefined || !this.meetingMinutes) {
        return false;
      }
      var time = this.formatTime(this.meetingHours, this.meetingMinutes);
      var slot = window.JoolmeSlots.slotsFor(this.meetingDate, this.serverSlots())
        .find(function (s) { return s.time === time; });
      return !!(slot && slot.available);
    },
    ensureValidSlotSelection: function () {
      var self = this;
      var hours = this.hourOptions;
      var hour = hours.find(function (o) { return o.hour == self.meetingHours && o.available; });
      if (!hour) {
        hour = hours.find(function (o) { return o.available; });
      }
      if (!hour) {
        this.meetingHours = null;
        this.meetingMinutes = null;
        return;
      }
      this.meetingHours = hour.hour;
      var minute = hour.minutes.find(function (m) { return m.minute == self.meetingMinutes && m.available; });
      if (!minute) {
        minute = hour.minutes.find(function (m) { return m.available; });
      }
      this.meetingMinutes = minute ? minute.minute : null;
    },
    confirmAndSubmit: function () {
      // Real validation: the slot is atomically reserved server-side before the CRM request goes out.
      var self = this;
      if (!this.checkSelectedSlot()) {
        alert('השעה הזו אינה זמינה, אפשר לבחור שעה אחרת?');
        this.refreshSlots();
        this.setStep(4);
        return;
      }
      this.reserving = true;
      var time = this.formatTime(this.meetingHours, this.meetingMinutes);
      window.JoolmeSlots.reserve(this.meetingDate, time, {
        name: this.clientName,
        phone: window.JoolmeSlots.normalizePhone(this.clientEmailPhone),
        source: this.slotSource || 'appointment'
      }).then(function (result) {
        self.reserving = false;
        if (!result || result.ok !== true) {
          var reason = result && result.reason;
          if (reason === 'full' || reason === 'past' || reason === 'invalid_slot') {
            alert('השעה הזו אינה זמינה, אפשר לבחור שעה אחרת?');
            self.refreshSlots();
            self.setStep(4);
          } else if (reason === 'multiple_existing') {
            if (confirm('מצאנו יותר מפגישה עתידית אחת עם השם והטלפון האלה. כדי שלא נשנה את הפגישה הלא נכונה, אפשר לפנות אלינו עכשיו בוואטסאפ.')) {
              window.open('https://wa.me/972559974891', '_blank');
            }
            self.setStep(5);
          } else if (reason === 'invalid_phone' || reason === 'invalid_name') {
            alert('כדי לקבוע או לשנות פגישה, צריך למלא שם מלא ומספר טלפון תקין.');
            self.setStep(5);
          } else {
            // Availability service unreachable/errored: fail closed, offer WhatsApp, stay on this step.
            if (confirm('יש בעיה זמנית בבדיקת זמינות. אפשר לנסות שוב בעוד רגע או לפנות אלינו בוואטסאפ.')) {
              window.open('https://wa.me/972559974891', '_blank');
            }
          }
          return;
        }
        self.submitForm();
        self.setStep(6);
        self.$nextTick(function () {
          var successTitle = self.$el.querySelector('.step--6 .step-title');
          if (!successTitle) return;
          if (result.action === 'rescheduled') {
            successTitle.textContent = 'הפגישה הקיימת הועברה למועד החדש';
          } else if (result.action === 'unchanged') {
            successTitle.textContent = 'הפגישה שלך כבר שמורה במועד הזה';
          }
        });
      });
    }
  }
};

/*
 * <joolme-phone-input v-model="clientEmailPhone" placeholder="...">
 * The wizard's phone field. A real tel input (numeric keypad, phone autofill), normalized on
 * every keystroke without v-model's composition guard, with a quiet hint under the field.
 * The hint space is always reserved so the form never shifts. The hint appears only after the
 * visitor leaves the field or pauses typing — never while they are mid-number.
 * Styles: assets/joolme-phone-input.css.
 */
if (window.Vue) {
  window.Vue.component('joolme-phone-input', {
    props: {
      value: { type: String, default: '' },
      placeholder: { type: String, default: '' }
    },
    data: function () {
      return { showHint: false, idleTimer: null };
    },
    computed: {
      status: function () {
        return window.JoolmeSlots.phoneStatus(this.value);
      },
      hintText: function () {
        if (this.status === 'incomplete') return '\u05e0\u05e8\u05d0\u05d4 \u05e9\u05d4\u05de\u05e1\u05e4\u05e8 \u05dc\u05d0 \u05de\u05dc\u05d0';
        if (this.status === 'invalid') return '\u05e0\u05e8\u05d0\u05d4 \u05e9\u05d9\u05e9 \u05d8\u05e2\u05d5\u05ea \u05d1\u05de\u05e1\u05e4\u05e8';
        return '';
      },
      hintVisible: function () {
        return this.showHint && this.hintText !== '';
      },
      hintId: function () {
        return 'joolme-phone-hint-' + this._uid;
      }
    },
    watch: {
      status: function (s) {
        if (s === 'valid' || s === 'empty') this.showHint = false;
      }
    },
    beforeDestroy: function () {
      clearTimeout(this.idleTimer);
    },
    methods: {
      onInput: function (e) {
        var el = e.target;
        var clean = window.JoolmeSlots.normalizePhone(el.value);
        if (el.value !== clean) el.value = clean;
        this.$emit('input', clean);
        clearTimeout(this.idleTimer);
        if (this.showHint) return;
        var self = this;
        this.idleTimer = setTimeout(function () {
          if (self.status === 'incomplete' || self.status === 'invalid') self.showHint = true;
        }, 1500);
      },
      onBlur: function () {
        clearTimeout(this.idleTimer);
        if (this.status === 'incomplete' || this.status === 'invalid') this.showHint = true;
      }
    },
    template:
      '<div class="joolme-phone">' +
        '<input type="tel" class="form-control" inputmode="tel" autocomplete="tel" autocorrect="off"' +
        ' autocapitalize="off" spellcheck="false" :placeholder="placeholder" :value="value"' +
        ' :aria-invalid="hintVisible ? \'true\' : \'false\'" :aria-describedby="hintId"' +
        ' @input="onInput" @blur="onBlur">' +
        '<div class="joolme-phone-hint" :id="hintId" :class="{ \'is-visible\': hintVisible }" aria-live="polite">{{ hintText }}</div>' +
      '</div>'
  });
}

/*
 * Legacy wizard copies (templates/product.liquid) still carry the old
 * <input type="text" v-model="clientEmailPhone"> in their markup. This script is loaded right
 * after that markup and before the wizard's `new Vue(...)`, so the uncompiled template is still
 * plain DOM here: swap the old input for <joolme-phone-input> so every copy of the popup gets the
 * same field. Templates already using <joolme-phone-input> have nothing to swap.
 * TODO: when product.liquid is next edited, write the component tag there and this becomes a no-op.
 */
(function () {
  var legacy = document.querySelectorAll('input[v-model="clientEmailPhone"]');
  if (!legacy.length) return;
  Array.prototype.forEach.call(legacy, function (input) {
    var field = document.createElement('joolme-phone-input');
    field.setAttribute('v-model', 'clientEmailPhone');
    field.setAttribute('placeholder', input.getAttribute('placeholder') || '');
    input.parentNode.replaceChild(field, input);
  });
  var script = document.currentScript;
  var src = script && script.src;
  if (src && !document.querySelector('link[href*="joolme-phone-input.css"]')) {
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = src.split('?')[0].replace(/joolme-appointment-slots\.js$/, 'joolme-phone-input.css');
    document.head.appendChild(link);
  }
})();
